/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, Flex, Text, useToast } from "@chakra-ui/react"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import React, { useCallback, useEffect, useRef } from "react"
import { FMPTableType, formatDate } from "../../../common/commonConstants"
import useErrorToast from "../../../common/commonHooks/errorToast"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { useReOrderFmpProductsMutation, useUpdateFmpProjectMutation, type UpdateFmpProjectMutation } from "../../../type-and-hooks"
import {
  editFmpFieldsDirtyAtom,
  editFmpFieldsSnapshotAtom,
  editFmpRowOrderDirtyAtom,
  editFmpSaveLoadingAtom,
  editFmpSaveTriggerAtom,
  type EditFmpFieldsSnapshot
} from "../common.fmp.hooks/editFmpDirty.store"
import { formData } from "../common.fmp.hooks/formData.store"
import FMPProjectTable from "../fmpProjectTable/fmpProjectTable"
import { isReorderModeAtom, sortedOrderAtom } from "../fmpProjectTable/fmpSortedOrder.store"

interface EditFireManagementPlanTableProps {
  fmpId: number
  refToPass: React.RefObject<HTMLDivElement>
}

export const EditFireManagementPlanTable = ({ fmpId, refToPass }: EditFireManagementPlanTableProps): JSX.Element => {
  const [userDetails] = useAtom(userDetailsAtom)
  const [formDataAtom] = useAtom(formData)
  const toast = useToast()
  const [updateFMPProjectMutation, { data: fmpUpdateSuccess, loading: fmpUpdateLoading, error: fmpUpdateError }] = useUpdateFmpProjectMutation()
  const [reorderProject, { data: reorderSuccess, loading: reorderProjectLoading, error: reorderProjectError }] = useReOrderFmpProductsMutation()
  // Error message for toast update failure
  const [getSortedOrderAtom] = useAtom(sortedOrderAtom)
  const setFieldsSnapshot = useSetAtom(editFmpFieldsSnapshotAtom)
  const setSaveTrigger = useSetAtom(editFmpSaveTriggerAtom)
  const setSaveLoading = useSetAtom(editFmpSaveLoadingAtom)
  const setIsReorderMode = useSetAtom(isReorderModeAtom)
  const fieldsDirty = useAtomValue(editFmpFieldsDirtyAtom)
  const rowOrderDirty = useAtomValue(editFmpRowOrderDirtyAtom)
  const tableRefetchRef = useRef<(() => void) | null>(null)
  const hasRefetchedRef = useRef<boolean>(false)
  // Tracks the field values actually submitted by the most recent save, and
  // which Apollo mutation result (by reference) has already been handled -
  // Apollo keeps returning the same truthy `data` on unrelated re-renders,
  // so watching it naively re-fires the toast/snapshot-reset on every
  // keystroke rather than only once per real save.
  const savedFieldsRef = useRef<EditFmpFieldsSnapshot | null>(null)
  const lastHandledUpdateRef = useRef<UpdateFmpProjectMutation | null | undefined>(null)
  useErrorToast({ error: reorderProjectError, toast, errorMessage: "Error reordering FMP" })
  useErrorToast({ error: fmpUpdateError, toast, errorMessage: "Error updating FMP" })

  const handleSave = (): void => {
    if (!userDetails?._id || !fmpId) {
      return
    }
    const { projectName, projectAddress, companyName, companyAddress, revision, companyLogo } = formDataAtom

    if (fieldsDirty) {
      savedFieldsRef.current = {
        projectName: projectName ?? "",
        companyName: companyName ?? "",
        projectAddress: projectAddress ?? "",
        companyAddress: companyAddress ?? "",
        revision: revision ?? ""
      }
      updateFMPProjectMutation({
        variables: {
          input: {
            id: fmpId,
            user_id: userDetails?._id,
            project_name: projectName,
            project_address: projectAddress,
            subcontractor_name: companyName,
            subcontractor_address: companyAddress,
            subcontractor_logo: companyLogo,
            revision
          }
        }
      })
    }
    if (rowOrderDirty && userDetails?._id) {
      reorderProject({
        variables: {
          fmpProjectId: fmpId,
          userId: userDetails?._id,
          input: getSortedOrderAtom.map((item, index) => ({ id: Number(item.id), serial_no: index + 1 }))
        }
      })
    }
  }
  // Showing a toast message when the FMP is updated successfully - only
  // once per actual mutation response, not on every unrelated re-render.
  useEffect(() => {
    if (fmpUpdateSuccess && fmpUpdateSuccess !== lastHandledUpdateRef.current) {
      lastHandledUpdateRef.current = fmpUpdateSuccess
      toast({
        title: "FMP updated successfully",
        status: "success",
        duration: 5000,
        isClosable: true
      })
      if (savedFieldsRef.current) {
        setFieldsSnapshot(savedFieldsRef.current)
      }
      setIsReorderMode(false)
    }
  }, [fmpUpdateSuccess, toast, setFieldsSnapshot, setIsReorderMode])

  // Expose the save action and its loading state to the project-info row,
  // which owns the only Save button now that the bottom Back/Save row is gone.
  useEffect(() => {
    setSaveTrigger(() => handleSave)
    return () => {
      setSaveTrigger(null)
    }
  }, [handleSave, setSaveTrigger])

  useEffect(() => {
    setSaveLoading(fmpUpdateLoading || reorderProjectLoading)
  }, [fmpUpdateLoading, reorderProjectLoading, setSaveLoading])

  // Handle reorder success and refetch table data
  useEffect(() => {
    if (reorderSuccess && tableRefetchRef.current && !hasRefetchedRef.current) {
      hasRefetchedRef.current = true
      toast({
        title: "FMP reordered successfully",
        status: "success",
        duration: 5000,
        isClosable: true
      })
      // Refetch table data to get the updated order
      tableRefetchRef.current()
      setIsReorderMode(false)

      // Reset the flag after a delay to allow for future reorders
      setTimeout(() => {
        hasRefetchedRef.current = false
      }, 1000)
    }
  }, [reorderSuccess, toast, setIsReorderMode])

  const handleRefetchAvailable = useCallback((refetchFn: () => void): void => {
    tableRefetchRef.current = refetchFn
  }, [])

  return (
    <Box mt="2" pos="relative">
      <Box ref={refToPass}>
        <FMPProjectTable fmpId={fmpId} tableType={FMPTableType.Edit_FMP} onRefetchAvailable={handleRefetchAvailable} />
      </Box>
      <Flex gap="8px" mt="2">
        {Boolean(formDataAtom.dateCreated) && <Text variant="RCaption">Date Created: {formatDate(formDataAtom.dateCreated)}</Text>}
        {Boolean(formDataAtom.dateModified) && <Text variant="RCaption">Date Modified: {formatDate(formDataAtom.dateModified)}</Text>}
      </Flex>
    </Box>
  )
}
